/**
 * FASTA ストリーム取込（I20 / U152）。
 * 全文を string にせずバイト単位で塩基コードへ変換し、チャンクごとにシンクへ渡す。
 * マルチ contig は配列複数として取り込む。
 */

import { MAX_SEQUENCE_LENGTH } from '../constants';
import { BYTE_FOREIGN, BYTE_IGNORE, BYTE_TO_CODE, NUCLEOTIDE_CODE_COUNT } from '../sequence/nucleotideCodes';
import { Topology } from '../sequence/interval';
import { ImportProgress, ImportResult, ImportSink } from './importSink';

const CODE_BUFFER_SIZE = 1 << 16;
const PROTEIN_SAMPLE_MIN = 1000;
const PROTEIN_FOREIGN_RATIO = 0.15;
const GREATER_THAN = 0x3e;
const SEMICOLON = 0x3b;

export interface FastaParseOptions {
  topology?: Topology;
  totalBytes?: number;
  maxSequenceLength?: number;
  onProgress?: (progress: ImportProgress) => void;
  /** 進捗通知の間隔（バイト） */
  progressIntervalBytes?: number;
  /** 名前が無いときの既定名 */
  defaultName?: string;
}

export class ProteinSequenceError extends Error {
  constructor(name: string) {
    super(`タンパク質配列は取り込めません: ${name}`);
    this.name = 'ProteinSequenceError';
  }
}

export class SequenceTooLongError extends Error {
  constructor(name: string, length: number, limit: number) {
    super(`配列が長すぎます: ${name} は ${length} bp（上限 ${limit} bp）`);
    this.name = 'SequenceTooLongError';
  }
}

export async function parseFastaStream(
  source: AsyncIterable<Uint8Array>,
  sink: ImportSink,
  options: FastaParseOptions = {}
): Promise<ImportResult> {
  const topology = options.topology ?? 'linear';
  const maxLength = options.maxSequenceLength ?? MAX_SEQUENCE_LENGTH;
  const progressInterval = options.progressIntervalBytes ?? 8 * 1024 * 1024;
  const decoder = new TextDecoder();
  const warnings: string[] = [];

  const codeBuffer = new Uint8Array(CODE_BUFFER_SIZE);
  let codeFilled = 0;

  let bytesRead = 0;
  let lastProgressAt = 0;
  let atLineStart = true;
  /** その行の読み方。`comment` は古い FASTA の `;` 行 */
  let lineMode: 'sequence' | 'header' | 'comment' = 'sequence';
  let headerBytes: number[] = [];

  let open = false;
  let sequenceCount = 0;
  let currentName = '';
  let currentLength = 0;
  let foreignCount = 0;
  let residueCount = 0;

  const flushCodes = async () => {
    if (codeFilled === 0) return;
    await sink.appendCodes(codeBuffer.subarray(0, codeFilled));
    codeFilled = 0;
  };

  const reportProgress = () => {
    if (!options.onProgress) return;
    options.onProgress({
      bytesRead,
      basesRead: currentLength,
      totalBytes: options.totalBytes,
      sequenceName: currentName,
    });
  };

  const closeSequence = async () => {
    if (!open) return;
    await flushCodes();
    if (currentLength === 0) {
      warnings.push(`塩基が無い記述を飛ばしました: ${currentName}`);
      await sink.endSequence({ length: 0, topology });
    } else {
      await sink.endSequence({ length: currentLength, topology });
    }
    open = false;
    currentLength = 0;
    foreignCount = 0;
    residueCount = 0;
  };

  const openSequence = async (headerText: string) => {
    const trimmed = headerText.trim();
    const spaceAt = trimmed.search(/\s/);
    const name = (spaceAt === -1 ? trimmed : trimmed.slice(0, spaceAt)) || options.defaultName || '配列';
    const description = spaceAt === -1 ? '' : trimmed.slice(spaceAt + 1).trim();
    currentName = name;
    await sink.beginSequence({ name, description, topology });
    open = true;
    sequenceCount += 1;
  };

  for await (const chunk of source) {
    for (let i = 0; i < chunk.length; i += 1) {
      const byte = chunk[i];

      if (byte === 0x0a) {
        if (lineMode === 'header') {
          await closeSequence();
          await openSequence(decoder.decode(Uint8Array.from(headerBytes)));
          headerBytes = [];
        }
        lineMode = 'sequence';
        atLineStart = true;
        continue;
      }

      if (lineMode === 'comment') continue;

      if (lineMode === 'header') {
        if (byte !== 0x0d) headerBytes.push(byte);
        continue;
      }

      if (atLineStart && (byte === GREATER_THAN || byte === SEMICOLON)) {
        // ';' は古い FASTA のコメント行。行末まで読み捨てる
        lineMode = byte === GREATER_THAN ? 'header' : 'comment';
        atLineStart = false;
        continue;
      }
      atLineStart = false;

      const code = BYTE_TO_CODE[byte];
      if (code === BYTE_IGNORE) continue;
      if (code === BYTE_FOREIGN) {
        foreignCount += 1;
        residueCount += 1;
        if (
          residueCount >= PROTEIN_SAMPLE_MIN &&
          foreignCount / residueCount > PROTEIN_FOREIGN_RATIO
        ) {
          throw new ProteinSequenceError(currentName || '無名');
        }
        continue;
      }
      if (code >= NUCLEOTIDE_CODE_COUNT) continue;

      if (!open) {
        // ヘッダ無しのプレーン配列（I27）
        await openSequence(options.defaultName ?? '配列');
      }

      residueCount += 1;
      codeBuffer[codeFilled] = code;
      codeFilled += 1;
      currentLength += 1;
      if (currentLength > maxLength) {
        throw new SequenceTooLongError(currentName, currentLength, maxLength);
      }
      if (codeFilled === CODE_BUFFER_SIZE) await flushCodes();
    }

    bytesRead += chunk.length;
    if (bytesRead - lastProgressAt >= progressInterval) {
      lastProgressAt = bytesRead;
      reportProgress();
    }
  }

  if (lineMode === 'header') {
    await closeSequence();
    await openSequence(decoder.decode(Uint8Array.from(headerBytes)));
  }
  await closeSequence();
  reportProgress();

  return { sequenceCount, bytesRead, warnings };
}
